import { type FC, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, DialogComponent } from "widgets";

import { IndexStatus, type ProjectFile, type TrackBedConfiguration, type TrackBedOrderedBedFile } from "@/graphql.ts";
import { isBedFile, isIndexRequiredForGenomeViewer } from "@/modules/files/utils/file.ts";
import { getProjectFilesById, type IFileState } from "@/redux/modules/files.ts";
import { type ISortableTrack, setGenomeBrowserBedTrackCustomization } from "@/redux/modules/genome-browser.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { DEFAULT_BED_COLOR, TRACK_TYPE } from "@/settings/settings.ts";

import { type FileSelection, TrackFilesSelection } from "../ngs-track/TrackFilesSelection.js";

export interface IBedTrackCustomizationDialogProps {
  onClose: () => void;
}
const BedsTrackCustomizationDialog: FC<IBedTrackCustomizationDialogProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const [selectedBEDFiles, setSelectedBEDFiles] = useState<FileSelection[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>(null);

  const bedTrack = useSelector<IAppState, ISortableTrack>((state) =>
    state.genomeBrowser.tracks.find((tr: ISortableTrack) => tr.type === TRACK_TYPE.BED),
  );
  const indexingStatusLookup = useSelector<IAppState, IFileState["indexingStatusLookup"]>(
    (state) => state.files.indexingStatusLookup,
  );

  useEffect(() => {
    // setup the initial selection
    const initiallySelectedBEDFiles: TrackBedOrderedBedFile[] =
      bedTrack?.trackCustomization?.bedConfiguration?.orderedBedFiles ?? [];
    const fileIds: number[] = initiallySelectedBEDFiles.map((file) => file.bedFileId);
    void dispatch(getProjectFilesById(fileIds)).then((files: ProjectFile[]) => {
      const initialSelection: FileSelection[] = files.map((file: ProjectFile) => {
        return {
          file: file,
          // if the file is not accessible/hidden then the id is set to zero unfortunately
          color: initiallySelectedBEDFiles.find((s) => s.bedFileId === file.id)?.color,
        };
      });
      setSelectedBEDFiles(initialSelection);
    });
  }, []);

  useEffect(() => {
    // validation
    if (selectedBEDFiles.some((bedFile) => bedFile.file.hidden || bedFile.file.deleted)) {
      setErrorMessage("Only accessible files should be selected");
    } else {
      setErrorMessage(null);
    }
  }, [selectedBEDFiles]);

  const applyCustomization = useCallback(() => {
    if (errorMessage) {
      return;
    }
    const initiallySelectedBEDFiles: TrackBedOrderedBedFile[] =
      bedTrack?.trackCustomization?.bedConfiguration?.orderedBedFiles ?? [];

    const configPayload: Partial<TrackBedConfiguration> = {
      orderedBedFiles: selectedBEDFiles.map((selectedBEDFile) => {
        const initialConfig = initiallySelectedBEDFiles.find(
          (bedFileData) => bedFileData.bedFileId === selectedBEDFile.file.id,
        );
        return {
          ...DEFAULT_BED_TRACK_CONFIGURATION,
          bedFileId: selectedBEDFile.file.id,
          ...initialConfig, // keep intact the rest of the configuration that is not configured in there (visibleCoverage, visiblePileup)
          color: selectedBEDFile.color,
        } satisfies TrackBedOrderedBedFile;
      }),
    };

    try {
      dispatch(setGenomeBrowserBedTrackCustomization(configPayload));
    } catch (error: Error) {
      setErrorMessage(error.message);
    }
    props.onClose();
  }, [errorMessage, selectedBEDFiles]);

  return (
    <DialogComponent
      title={"BED Track"}
      mainButton={{
        label: "Apply",
        onPress: applyCustomization,
      }}
      errorCallout={{ message: errorMessage }}
      closeFn={props.onClose}
      width={500}
    >
      <Card noBorder noPadding>
        <TrackFilesSelection
          selectedFiles={selectedBEDFiles}
          setSelectedFiles={(files: FileSelection[]) => setSelectedBEDFiles(files)}
          isSelectableFilter={(file) => {
            // @see FileNameCell.tsx
            const indexStatus = indexingStatusLookup[file.id] || file.indexingStatus;
            return (
              isBedFile(file) && (isIndexRequiredForGenomeViewer(file) ? indexStatus === IndexStatus.Exists : true)
            );
          }}
          baseFilterExtension="BED"
          baseFilterHint="Only .bed files can be selected."
          indexingStatusLookup={indexingStatusLookup}
          defaultColor={DEFAULT_BED_COLOR}
        />
      </Card>
    </DialogComponent>
  );
};

export default BedsTrackCustomizationDialog;

export const DEFAULT_BED_TRACK_CONFIGURATION: TrackBedOrderedBedFile = {
  bedFileId: null,
  color: DEFAULT_BED_COLOR,
  collapsed: false,
};
